import { Collapse, List, ListItem, ListItemText } from '@material-ui/core';

import { ExpandIcon } from '../expand-icon';
import { TitleOfCustom } from './components';
import { CardProduct } from '../card-product';
import { CardCustomization } from '../card-customization';
import { ItemOutOfStockDate } from '../item-out-of-stock-date';

import { useRenderOnlyProductsAndCustoms } from './hooks';

import makeClasses from './RenderOnlyProductsAndCustoms.style';

import type {
  FullMenuCatalog,
  IMenuCustomization,
  IMenuProduct,
} from 'features/menu-page/domain';

export function RenderOnlyProductsAndCustoms(props: Props) {
  const {
    title,
    ariaLabel,
    isModeOutOfStock = false,
    catalog,
    products = [],
    customizations = [],
    toggleOnOffProduct,
    toggleOnOffCustomization,
  } = props;

  const {
    lang,
    dataListIdToProducts,
    dataListIdToCustoms,
    collapse,
    handleClickCollapse,
  } = useRenderOnlyProductsAndCustoms();

  const _classes = makeClasses();

  return (
    <List component='section' aria-label={ariaLabel}>
      <ListItem component='h1' classes={{ root: _classes.title }}>
        {title}
      </ListItem>

      <ListItem
        button
        component='button'
        data-list-id={dataListIdToProducts}
        onClick={handleClickCollapse}
        classes={{ root: _classes.list }}
        style={{ display: products.length ? 'flex' : 'none' }}
      >
        <ExpandIcon
          isExpanded={collapse.products}
          className={customizations.length === 0 ? 'displayNone' : ''}
        />
        <ListItemText
          classes={{ primary: _classes.listText }}
          primary={`${lang.products.title} (${products.length})`}
        />
      </ListItem>
      <Collapse
        mountOnEnter
        unmountOnExit
        aria-label='out-of-stock-products-list-collapse'
        in={collapse.products}
      >
        <List disablePadding aria-label='out-of-stock-products-list'>
          {products.map(product => (
            <CardProduct
              key={`product-${product.id}`}
              product={product}
              toggleOnOff={toggleOnOffProduct}
            >
              {isModeOutOfStock && (
                <ItemOutOfStockDate
                  shutdownDate={product.suspensionInfo.shutdownDate?.slice(
                    0,
                    10,
                  )}
                  activationDate={product.suspensionInfo.activationDate?.slice(
                    0,
                    10,
                  )}
                />
              )}
            </CardProduct>
          ))}
        </List>
      </Collapse>

      <ListItem
        button
        component='button'
        data-list-id={dataListIdToCustoms}
        onClick={handleClickCollapse}
        classes={{ root: _classes.list }}
        style={{ display: customizations.length ? 'flex' : 'none' }}
      >
        <ExpandIcon
          isExpanded={collapse.customs}
          className={products.length === 0 ? 'displayNone' : ''}
        />
        <ListItemText
          classes={{ primary: _classes.listText }}
          primary={`${lang.customs.title} (${customizations.length})`}
        />
      </ListItem>
      <Collapse
        mountOnEnter
        unmountOnExit
        aria-label='customization-list-collapse'
        in={collapse.customs}
      >
        <List disablePadding aria-label='customization-list'>
          {customizations.map((customItem, customItemIndex) => (
            <CardCustomization
              key={`custom-item-${customItem.id}`}
              customization={customItem}
              renderIndex={customItemIndex}
              toggleOnOff={toggleOnOffCustomization}
              title={
                <TitleOfCustom customId={customItem.id} catalog={catalog} />
              }
            >
              {isModeOutOfStock && (
                <ItemOutOfStockDate
                  shutdownDate={customItem.suspensionInfo.shutdownDate?.slice(
                    0,
                    10,
                  )}
                  activationDate={customItem.suspensionInfo.activationDate?.slice(
                    0,
                    10,
                  )}
                />
              )}
            </CardCustomization>
          ))}
        </List>
      </Collapse>
    </List>
  );
}

interface Props {
  title: string;
  ariaLabel: string;
  isModeOutOfStock?: boolean;
  catalog: FullMenuCatalog;
  products?: IMenuProduct[];
  customizations?: IMenuCustomization[];
  toggleOnOffProduct: (c: boolean, p: IMenuProduct) => void;
  toggleOnOffCustomization: (c: boolean, item: IMenuCustomization) => void;
}
